import type { IDocument } from "@bbj-types/document";
import type { DocumentTypes } from "@bbj-types/document/enum";
import type { ObjectId } from "@bbj-types/utils";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { SendMailModalActions } from "./actions";
import { SendMailModalContent } from "./content";
import { useSendMailModal } from "./logics";
import type { TSendMailModalCandidate } from "./type";

export interface ISendMailModalProps {
  open: boolean;
  candidates: TSendMailModalCandidate[];
  templateTypeDoc: DocumentTypes.WORKSHOP_MAIL_TEMPLATE;
  getMailPreview: (templateId: ObjectId) => Promise<string | undefined>;
  onSubmit: (templateId: IDocument, candidateIds: ObjectId[], coachingStep?: string) => Promise<void>;
  onSubmitLoading: boolean;
  onClose: () => void;
  onParticipantsModified?: (modifiedParticipants: Set<string>) => void;
}

export const SendMailModal = ({
  open,
  candidates,
  templateTypeDoc,
  getMailPreview,
  onSubmit,
  onSubmitLoading,
  onClose,
  onParticipantsModified,
}: ISendMailModalProps) => {
  const {
    step,
    setStep,
    coachingStepChange,
    setCoachingStepChange,
    createAction,
    setCreateAction,
    recipients,
    setRecipients,
    template,
    setTemplate,
    templatePreviewLoading,
    setTemplatePreviewLoading,
    invalidRecipientsCount,
    handleSubmit,
    handleClose,
    loading,
  } = useSendMailModal(candidates, onSubmit, onClose, onParticipantsModified);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle component={Stack} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" noWrap>
          Envoi d'un courrier de convocation
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="medium" color="primary" />
        </IconButton>
      </DialogTitle>
      <SendMailModalContent
        step={step}
        recipients={recipients}
        setRecipients={setRecipients}
        coachingStepChange={coachingStepChange}
        setCoachingStepChange={setCoachingStepChange}
        createAction={createAction}
        setCreateAction={setCreateAction}
        template={template}
        setTemplate={setTemplate}
        templatePreviewLoading={templatePreviewLoading}
        setTemplatePreviewLoading={setTemplatePreviewLoading}
        getMailPreview={getMailPreview}
        templateTypeDoc={templateTypeDoc}
        invalidRecipientsCount={invalidRecipientsCount}
      />
      <SendMailModalActions
        step={step}
        setStep={setStep}
        onSubmit={handleSubmit}
        onClose={handleClose}
        loading={onSubmitLoading || loading}
        submitDisabled={!template || templatePreviewLoading}
      />
    </Dialog>
  );
};
